import {
	EventType,
	MessageType,
	IChatEventChannelOpenMessage,
	IChatMessagesOut,
	CloseEventType,
	MinMaxSource,
	SenderSource,
	isCommandMessageMaximizeChat,
	isCommandMessageMINMAXChat,
	isCommandMessageFormFullscreen,
	isCommandWebMessage,
} from '@enuan/chat__utils';
import { RefObject, useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { messagesSlice } from '../../reduxStore/slices/messages.slice';
import { messagesStackSlice } from '../../reduxStore/slices';
import { useAddReceivedMessage, useSendMessage } from './messages.hooks';
import { frameStateContext } from '../../contexts/frame/context';
import { ClientMessage } from '../../utils/ClientMessage';
import { ChannelState } from '../../interfaces/ChannelState';
import { useLogError } from '../../components/ErrorBoundary/useLogError';
import { configContext } from 'src/contexts/config';
import { useUpdateParams } from './updateParams.hooks';
import { useMessageSideEffectsHandler } from './messageSideEffects.hooks';
import { useReconnect } from './connection.hooks';
import { instanceContext } from 'src/contexts/instance';
import {
	NORMAL_CLOSE_CODE,
	type IUseChatProps,
	type ProviderOnOpen,
	type ProviderOnChatMessage,
	type ProviderOnChatEcho,
	type ProviderOnChatError,
	type ProviderOnClose,
	type ProviderOnError,
} from './types';

type UseProviderListenersParams = {
	params: IUseChatProps['params'];
	onConnection: IUseChatProps['onConnection'];
	onWebMessage: IUseChatProps['onWebMessage'];
	timestampRef: RefObject<string | undefined>;
	startConnection: () => unknown;
	resetRetries: () => void;
};

/**
 * Builds every provider callback internally and subscribes them to
 * the provider instance. Automatically unsubscribes on cleanup.
 */
export const useProviderListeners = ({
	params,
	onConnection,
	onWebMessage,
	timestampRef,
	startConnection,
	resetRetries,
}: UseProviderListenersParams) => {
	const { instance: provider } = useContext(instanceContext);
	const dispatch = useDispatch();
	const logError = useLogError('init');
	const credentialRefreshAttemptedRef = useRef(false);
	const resetCredentialRefresh = useCallback(() => {
		credentialRefreshAttemptedRef.current = false;
	}, []);

	const onOpen = useOnOpenConnection({
		params,
		onConnection,
		resetRetries,
		resetCredentialRefresh,
	});
	const onChatMessage = useOnChatMessage({ timestampRef, onWebMessage });
	const onClose = useOnCloseConnection(
		startConnection,
		credentialRefreshAttemptedRef,
	);

	const onChatEcho = useCallback<ProviderOnChatEcho>(
		({ value: { timestamp, client_id: id } }) => {
			dispatch(messagesSlice.actions.setEchoTimestamp({ id, timestamp }));
		},
		[dispatch],
	);

	const onChatError = useCallback<ProviderOnChatError>(
		(event) =>
			logError('onChatMessageError', {
				error: new Error('OutErrorMessage'),
				payload: event.value,
			}),
		[logError],
	);

	const onError = useCallback<ProviderOnError>(
		(event) =>
			logError('onErrorConnection', {
				error: new Error('Connection Error'),
				payload: event,
			}),
		[logError],
	);

	useEffect(() => {
		const unsubOpen = provider?.onOpen(onOpen);
		const unsubChatMessage = provider?.onChatMessage(onChatMessage);
		const unsubChatEcho = provider?.onChatEcho(onChatEcho);
		const unsubChatError = provider?.onChatError(onChatError);
		const unsubClose = provider?.onClose(onClose);
		const unsubError = provider?.onError(onError);

		return () => {
			unsubOpen?.();
			unsubChatMessage?.();
			unsubChatEcho?.();
			unsubChatError?.();
			unsubClose?.();
			unsubError?.();
		};
	}, [
		provider,
		onOpen,
		onChatMessage,
		onChatEcho,
		onChatError,
		onClose,
		onError,
	]);
};

type UseOnOpenConnectionParams = {
	params: IUseChatProps['params'];
	onConnection: IUseChatProps['onConnection'];
	resetRetries: () => void;
	resetCredentialRefresh: () => void;
};

/**
 * Handles the connection-open event: resets retries, notifies the caller,
 * sends the CHANNEL_OPEN message and updates params.
 */
const useOnOpenConnection = ({
	params,
	onConnection,
	resetRetries,
	resetCredentialRefresh,
}: UseOnOpenConnectionParams) => {
	const { setChannelState } = useContext(configContext);
	const sendMessage = useSendMessage();
	const updateParams = useUpdateParams();
	const logError = useLogError('init');

	return useCallback<ProviderOnOpen>(
		(event) => {
			console.log('Connection Open');
			resetRetries();
			resetCredentialRefresh();
			onConnection?.(event);

			const channelOpen: IChatMessagesOut<IChatEventChannelOpenMessage> = {
				_type: MessageType.EVENT,
				_data: {
					name: EventType.CHANNEL_OPEN,
					parameters: {
						location: window.location.href,
						__params__: params,
					},
				},
			};
			sendMessage(channelOpen, true)
				.then(() => {
					setChannelState(ChannelState.CHANNEL_OPEN);
					updateParams(params);
				})
				.catch((error: unknown) =>
					logError('onOpenConnection:channelOpen', {
						error,
						payload: channelOpen,
					}),
				);
		},
		[
			params,
			sendMessage,
			onConnection,
			setChannelState,
			updateParams,
			resetRetries,
			resetCredentialRefresh,
			logError,
		],
	);
};

type UseOnChatMessageParams = {
	timestampRef: RefObject<string | undefined>;
	onWebMessage: IUseChatProps['onWebMessage'];
};

/**
 * Handles each incoming chat message: updates the timestamp ref, applies
 * side-effects, toggles min/max, and dispatches to the appropriate store slice.
 */
const useOnChatMessage = ({
	timestampRef,
	onWebMessage,
}: UseOnChatMessageParams) => {
	const messageHandler = useMessageSideEffectsHandler();
	const dispatch = useDispatch();
	const { toggleChat } = useContext(frameStateContext);
	const addMessage = useAddReceivedMessage();

	return useCallback<ProviderOnChatMessage>(
		({ value: message }) => {
			if (!message.timestamp) return;

			timestampRef.current = message._timestamp;

			if (isCommandWebMessage(message)) {
				onWebMessage?.(message);
			}

			messageHandler([message]);

			if (isCommandMessageMINMAXChat(message)) {
				toggleChat(isCommandMessageMaximizeChat(message), MinMaxSource.M3);
			}

			const clientMessage = ClientMessage(message, {
				sent: message.timestamp,
			});
			if (
				(message.sender === SenderSource.AGENT ||
					message.sender === SenderSource.SYSTEM) &&
				!isCommandMessageFormFullscreen(message)
			) {
				dispatch(messagesStackSlice.actions.addMessage(clientMessage));
			} else {
				addMessage(clientMessage);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- timestampRef is a ref
		[dispatch, toggleChat, addMessage, onWebMessage, messageHandler],
	);
};

/**
 * Handles unexpected connection close: transitions to RECONNECTING
 * and triggers the reconnect countdown. Ignores normal (1000) closes.
 */
const useOnCloseConnection = (
	startConnection: () => unknown,
	credentialRefreshAttemptedRef: { current: boolean },
) => {
	const { setChannelState } = useContext(configContext);
	const { refreshCredentials } = useContext(instanceContext);
	const logError = useLogError('init');
	const reconnect = useReconnect(startConnection);

	return useCallback<ProviderOnClose>(
		(event) => {
			console.warn('Connection closed', event);
			if (event.data?.code === NORMAL_CLOSE_CODE) return;
			setChannelState(ChannelState.DISCONNECTED);
			setChannelState(ChannelState.RECONNECTING);

			const scheduleReconnect = () =>
				reconnect().catch((error: unknown) => {
					logError('onCloseConnection:reconnection', { error });
					setChannelState(ChannelState.DISCONNECTED);
				});

			if (
				event.reason === CloseEventType.CONNECTION_FAILED &&
				!credentialRefreshAttemptedRef.current
			) {
				credentialRefreshAttemptedRef.current = true;
				refreshCredentials()
					.catch((error: unknown) => {
						logError('onCloseConnection:refreshCredentials', { error });
					})
					.finally(scheduleReconnect);
				return;
			}

			scheduleReconnect();
		},
		[
			setChannelState,
			logError,
			reconnect,
			refreshCredentials,
			credentialRefreshAttemptedRef,
		],
	);
};
